import { isAndroid } from 'src/utils/platform';
import { env } from 'src/config/env';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { PRODUCTION_APP_ORIGIN, STAGING_APP_ORIGIN } from 'src/config/externalLinks';
import { reportError } from 'src/modules/logs/Sentry';
import { SplashScreen } from 'src/modules/splash-screen';
import FirebaseConfigPlugin from 'src/plugins/FirebaseConfigPlugin';

export type SwitchEnvironmentType = 'staging' | 'production';

const UPDATE_URL_PROD = `${PRODUCTION_APP_ORIGIN}/api/app_versions/latest`;
const UPDATE_URL_STAGING = `${STAGING_APP_ORIGIN}/api/app_versions/latest`;

const ENVIRONMENT_OVERRIDE_KEY = 'baobab_environment_override';

export const getStoredEnvironmentOverride =
  (): SwitchEnvironmentType | null => {
    try {
      const stored = localStorage.getItem(ENVIRONMENT_OVERRIDE_KEY);
      if (stored === 'staging' || stored === 'production') {
        return stored;
      }
    } catch (error) {
      reportError(
        'localStorage not available in getStoredEnvironmentOverride',
        error,
      );
    }
    return null;
  };

const setStoredEnvironmentOverride = (env: SwitchEnvironmentType): void => {
  try {
    localStorage.setItem(ENVIRONMENT_OVERRIDE_KEY, env);
  } catch (error) {
    reportError(
      'localStorage not available in setStoredEnvironmentOverride',
      error,
    );
  }
};

const getUpdateUrlForEnvironment = (env: SwitchEnvironmentType): string => {
  return env === 'production' ? UPDATE_URL_PROD : UPDATE_URL_STAGING;
};

export const applyUpdateUrl = async (): Promise<void> => {
  const environmentOverride = getStoredEnvironmentOverride();

  if (!environmentOverride) {
    const isProduction = env.mode === 'production';
    await CapacitorUpdater.setUpdateUrl({
      url: isProduction ? UPDATE_URL_PROD : UPDATE_URL_STAGING,
    });
    return;
  }

  if (isAndroid()) {
    const current = await FirebaseConfigPlugin.getEnvironment();
    if (current.environment !== environmentOverride) {
      await FirebaseConfigPlugin.setEnvironment({
        environment: environmentOverride,
      });
    }
  }

  await CapacitorUpdater.setUpdateUrl({
    url: getUpdateUrlForEnvironment(environmentOverride),
  });
};

export const switchToEnvironment = async (
  targetEnv: SwitchEnvironmentType,
): Promise<void> => {
  setStoredEnvironmentOverride(targetEnv);
  if (isAndroid()) {
    try {
      // On Android, setEnvironment saves the preference and restarts the app. Firebase
      // reinitializes on startup with the correct config.
      await FirebaseConfigPlugin.setEnvironment({ environment: targetEnv });
      // App restarts above — code below won't execute on Android
    } catch (error) {
      reportError('Failed to switch Firebase environment on Android', error);
    }
  }
  await SplashScreen.withReloadCover(
    // eslint-disable-next-line no-restricted-syntax -- this is the covered call
    () => CapacitorUpdater.reload(),
    'environment_switch',
  );
};
